import React, { useEffect, useState } from "react";

import HttpManager from "../../services/HttpManager";

export interface KitPaymentPromptProps {
	campusId: number;
	kitIds: number[];
	studentName: string;
	/** Lo que falta por pagar de esos kits; se propone como monto. */
	pendingAmount?: number | null;
	onDone: (intentId: number, message: string) => void;
	onCancel: () => void;
}

/**
 * "Reportar pago": el alumno ya pagó por link o depósito y Ventas sube el comprobante.
 * Nace un ticket en Pagos por Procesar y Caja lo aplica; mientras tanto el kit no se anula.
 * Sin <label>: el CSS global los posiciona absolutos.
 */
const KitPaymentPrompt: React.FC<KitPaymentPromptProps> = ({ campusId, kitIds, studentName, pendingAmount, onDone, onCancel }) => {
	const [typeId, setTypeId] = useState<number>(2);
	const [amount, setAmount] = useState<string>(pendingAmount ? String(pendingAmount) : "");
	const [reference, setReference] = useState<string>("");
	const [paidAt, setPaidAt] = useState<string>("");
	const [note, setNote] = useState<string>("");
	const [files, setFiles] = useState<File[]>([]);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => { if (event.key === "Escape" && !busy) onCancel(); };
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [busy, onCancel]);

	const valid = files.length > 0 && (amount === "" || Number(amount) > 0);

	const submit = async () => {
		if (!valid) return;
		setBusy(true);
		setError(null);
		try {
			const response = await HttpManager.getInstance().kitsService.reportPayment(campusId, kitIds, {
				typeId, amount: amount === "" ? null : Number(amount), reference: reference.trim(), paidAt, note: note.trim()
			}, files);
			onDone(response.data.intentId, response.data.message);
		} catch (requestError: any) {
			const payload = requestError?.response?.data;
			const first = payload?.errors ? Object.values(payload.errors)[0] : null;
			setError(first ? String((first as any)[0]) : (payload?.message || "No se pudo reportar el pago."));
		} finally {
			setBusy(false);
		}
	};

	const caption: React.CSSProperties = { fontSize: "11px", color: "var(--wa-apagado)", lineHeight: 1, marginBottom: "3px", display: "block" };
	const input: React.CSSProperties = { height: "34px", fontSize: "13px" };
	const typeButton = (value: number, text: string) => <button key={value} type="button" disabled={busy}
		className={"btn btn-sm rounded-pill " + (typeId === value ? "btn-primary" : "btn-outline-secondary")} onClick={() => setTypeId(value)}>{text}</button>;

	return <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2100, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
		onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onCancel(); }}>
		<div className="card" style={{ width: "100%", maxWidth: "520px", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 10px 40px rgba(43,28,22,0.25)" }} role="dialog" aria-modal="true">
			<div className="card-body">
				<h4 style={{ marginBottom: "4px" }}>Reportar pago</h4>
				<div style={{ fontSize: "13px", color: "var(--wa-apagado)", marginBottom: "12px" }}>{studentName}{kitIds.length > 1 ? " · " + kitIds.length + " productos" : ""}</div>

				<div style={{ display: "flex", gap: "6px", marginBottom: "12px" }}>
					{typeButton(2, "Link de pago")}
					{typeButton(1, "Depósito o transferencia")}
				</div>

				<div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
					<div style={{ flex: "1 1 140px" }}>
						<span style={caption}>Monto</span>
						<input type="number" min={0} step="0.01" className="form-control" style={input} value={amount} disabled={busy} onChange={(event) => setAmount(event.target.value)} placeholder={pendingAmount ? String(pendingAmount) : ""} />
					</div>
					<div style={{ flex: "1 1 160px" }}>
						<span style={caption}>Referencia</span>
						<input type="text" className="form-control" style={input} value={reference} disabled={busy} maxLength={100} onChange={(event) => setReference(event.target.value)} />
					</div>
					<div style={{ flex: "1 1 140px" }}>
						<span style={caption}>Fecha del pago</span>
						<input type="date" className="form-control" style={input} value={paidAt} disabled={busy} onChange={(event) => setPaidAt(event.target.value)} />
					</div>
				</div>

				<div style={{ marginTop: "10px" }}>
					<span style={caption}>Comprobante <span style={{ color: "var(--wa-oro)" }}>*</span></span>
					<input type="file" accept="image/*,.pdf" multiple className="form-control" style={{ fontSize: "12px" }} disabled={busy}
						onChange={(event) => setFiles(event.target.files ? Array.from(event.target.files) : [])} />
					{files.length > 0 ? <div style={{ fontSize: "12px", color: "var(--wa-apagado)", marginTop: "4px" }}>{files.map((file) => file.name).join(" · ")}</div> : null}
				</div>

				<div style={{ marginTop: "10px" }}>
					<span style={caption}>Nota para Caja</span>
					<textarea className="form-control" rows={2} maxLength={2000} value={note} disabled={busy} onChange={(event) => setNote(event.target.value)} style={{ fontSize: "13px", resize: "vertical" }} />
				</div>

				{error ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
				<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px" }}>
					<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onCancel}>Cancelar</button>
					<button type="button" className="btn btn-primary rounded-pill" disabled={busy || !valid} onClick={submit}>{busy ? "Enviando…" : "Enviar a Pagos por Procesar"}</button>
				</div>
			</div>
		</div>
	</div>;
};

export default KitPaymentPrompt;
